// useDebouncedQueryUpdater.js
import { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom-v5-compat';
import qs from 'qs';

/**
 * useDebouncedQueryUpdater
 * 
 * @param {number} delay - debounce delay in ms
 * @param {Object} options
 * @param {boolean} options.replaceAll - if true, replaces all query params instead of merging
 * 
 * @returns {(filters: object) => void} updateFilters - function to call with new filters
 */
export function useQueryStringUpdater(delay = 150, { replaceAll = false } = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  const debouncedNavigate = useMemo(
    () =>
      debounce((filters, currentLocation) => {
        const currentQuery = qs.parse(currentLocation.search, {ignoreQueryPrefix: true,duplicates:"combine"});

        const newQuery = replaceAll ? filters : { ...currentQuery, ...filters };

        const queryString = qs.stringify(newQuery,  {addQueryPrefix: true,skipNulls: true, indices: false });
        const newUrl = queryString
          ? `${currentLocation.pathname}${queryString}`
          : currentLocation.pathname;
        navigate(newUrl, { replace: true });
      }, delay),
    [navigate, delay, replaceAll]
  );

  useEffect(() => {
    return () => {
      debouncedNavigate.cancel();
    };
  }, [debouncedNavigate]);

  const updateFilters = useCallback(
    (filters) => {
      debouncedNavigate(filters, location);
    },
    [location, debouncedNavigate]
  );

  return updateFilters;
}
